import React, { useRef, useState } from "react";
import { message, Tooltip } from "antd";
import { toSvg } from "html-to-image";

/**
 * Shared download-as-PNG machinery (button, frame, and the export pipeline).
 * Lives under components/common so shared UI (Table, charts) and utils can use
 * it without depending on a domain folder; opdClaims/primitives re-exports it.
 *
 * Renders a DOM node to a PNG file download (up to 3x pixel ratio — print/PPT
 * quality). Uses html-to-image (SVG foreignObject), so the export matches the
 * on-screen rendering exactly — pills, toggles, and webfonts included.
 */

// html-to-image's getFontEmbedCSS only keeps @font-face rules for families it
// finds on HTML elements inside the captured node — SVG text (Highcharts) is
// skipped, and a result computed for one node misses families other nodes use.
// Build the embed CSS ourselves from every @font-face rule in the document,
// with the font files inlined as data URLs, and reuse it for every export.
let fontEmbedCssPromise = null;

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const buildFontEmbedCss = async () => {
  const fontFaceRules = [];
  Array.from(document.styleSheets).forEach((sheet) => {
    let rules;
    try {
      rules = sheet.cssRules;
    } catch (e) {
      return; // cross-origin stylesheet — rules unreadable, nothing to embed
    }
    Array.from(rules || []).forEach((rule) => {
      if (rule.type === CSSRule.FONT_FACE_RULE) fontFaceRules.push(rule);
    });
  });

  const dataUrlByHref = {};
  const inlineFontUrls = async (rule) => {
    const baseHref = (rule.parentStyleSheet && rule.parentStyleSheet.href) || document.baseURI;
    let cssText = rule.cssText;
    const urlRefs = cssText.match(/url\((['"]?)[^'")]+\1\)/g) || [];
    for (const ref of urlRefs) {
      try {
        const rawUrl = ref.replace(/url\((['"]?)([^'")]+)\1\)/, "$2");
        if (rawUrl.startsWith("data:")) continue;
        const href = new URL(rawUrl, baseHref).href;
        if (!dataUrlByHref[href]) {
          dataUrlByHref[href] = fetch(href).then((res) => res.blob()).then(blobToDataUrl);
        }
        cssText = cssText.replace(ref, `url(${await dataUrlByHref[href]})`);
      } catch (e) {
        // font file unreachable — leave the original URL in place
      }
    }
    return cssText;
  };

  const cssTexts = await Promise.all(fontFaceRules.map(inlineFontUrls));
  return cssTexts.join("\n");
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not decode the exported SVG image"));
    img.src = src;
  });

// Characters that are legal in HTML/JS strings but ILLEGAL in XML 1.0. If API
// data contains one (control chars, lone surrogates), the serialized SVG fails
// to parse and the browser refuses to decode the whole image.
// The surrogate-pair alternative is listed first so valid pairs match as a
// unit (and are kept by the replacer); only then can a surrogate match alone,
// which means it is unpaired and must be dropped. No lookbehind — a regex
// lookbehind is a parse-time SyntaxError on Safari <= 16.3 and would take the
// whole bundle down with it.
const XML_INVALID_CHARS =
  // eslint-disable-next-line no-control-regex
  /[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDFFF]|[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g;

const stripXmlInvalidChars = (text) =>
  text.replace(XML_INVALID_CHARS, (match) => (match.length === 2 ? match : ""));

const svgTextToBlobUrl = (svgText) =>
  URL.createObjectURL(new Blob([svgText], { type: "image/svg+xml;charset=utf-8" }));

/** Log the text surrounding an XML parse error's line/column for diagnosis. */
const logParseErrorContext = (svgText, parseErrorText) => {
  console.error("Exported SVG is not valid XML:", parseErrorText);
  const match = /line (\d+) at column (\d+)/.exec(parseErrorText);
  if (!match) return;
  const line = svgText.split("\n")[Number(match[1]) - 1] || "";
  const col = Number(match[2]);
  console.error(
    "SVG text around the error:",
    JSON.stringify(line.slice(Math.max(0, col - 300), col + 100))
  );
};

/**
 * Re-parse invalid SVG text with the lenient HTML parser (which never fails)
 * and re-serialize it — the serializer emits well-formed XML, dropping only
 * whatever the broken markup made unparseable.
 */
const repairSvgText = (svgText) => {
  const doc = new DOMParser().parseFromString(svgText, "text/html");
  const svg = doc.querySelector("svg");
  if (!svg) return null;
  // The lenient parse can leave junk attributes (fragments of the broken
  // markup) whose names are not valid XML — drop them before re-serializing.
  const validName = /^[A-Za-z_][A-Za-z0-9_.:-]*$/;
  svg.querySelectorAll("*").forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      if (!validName.test(attr.name)) el.removeAttribute(attr.name);
    });
  });
  return new XMLSerializer().serializeToString(svg);
};

const svgTextToDataUrl = (svgText) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;

// White breathing room around every export, in CSS px. A card's border sitting flush against the image
// edge reads as a screenshot that was cropped too tightly, and pasted into a deck or a doc it collides
// with whatever is beside it. Applied here rather than at the call sites because every export in the app
// funnels through this one function.
const EXPORT_PADDING_PX = 12;

/** Load the SVG, draw it to a canvas, export PNG. Throws if any step fails. */
const rasterizeSvgSrc = async (src, width, height, pixelRatio) => {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  const drawWidth = Math.round(width * pixelRatio);
  const drawHeight = Math.round(height * pixelRatio);
  // Scaled with the pixel ratio so the margin is EXPORT_PADDING_PX CSS px at any export resolution
  // rather than that many device px.
  const inset = Math.round(EXPORT_PADDING_PX * pixelRatio);
  canvas.width = drawWidth + inset * 2;
  canvas.height = drawHeight + inset * 2;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, inset, inset, drawWidth, drawHeight);
  // Throws SecurityError if the browser considers the canvas tainted.
  return canvas.toDataURL("image/png");
};

/**
 * Rasterize the exported SVG text to a PNG data URL. Tries, in order:
 * 1. data: URL — origin-clean for foreignObject SVGs in every browser;
 * 2. Blob object URL — in case the data: URL is too large to decode;
 * 3. lenient repair re-serialization — in case the SVG is invalid XML.
 */
const svgTextToPng = async (svgText, width, height, pixelRatio) => {
  try {
    return await rasterizeSvgSrc(svgTextToDataUrl(svgText), width, height, pixelRatio);
  } catch (dataUrlError) {
    const parsed = new DOMParser().parseFromString(svgText, "image/svg+xml");
    const parseError = parsed.querySelector("parsererror");
    if (parseError) {
      logParseErrorContext(svgText, parseError.textContent);
      const repaired = repairSvgText(svgText);
      if (repaired) {
        return rasterizeSvgSrc(svgTextToDataUrl(repaired), width, height, pixelRatio);
      }
      throw dataUrlError;
    }
    const blobUrl = svgTextToBlobUrl(svgText);
    try {
      return await rasterizeSvgSrc(blobUrl, width, height, pixelRatio);
    } finally {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    }
  }
};

// Safari (and iOS WebKit) silently returns a blank image from toDataURL once
// the canvas passes ~16.7 million pixels. Cap the export's total pixel area
// so tall tables rasterize at a lower ratio instead of failing silently.
const MAX_CANVAS_AREA_PX = 16000000;

// Ref-counted visibility toggling: two exports can overlap on the same
// element (a capture node nested inside another one), and a plain
// save-and-restore would let the first finisher's restore be clobbered by
// the second's stale saved value, leaving the element invisible.
const hiddenElState = new Map();

const hideIgnoredEls = (node) => {
  const els = Array.from(node.querySelectorAll("[data-download-ignore]"));
  els.forEach((el) => {
    const state = hiddenElState.get(el);
    if (state) {
      state.count += 1;
      return;
    }
    hiddenElState.set(el, { count: 1, visibility: el.style.visibility });
    el.style.visibility = "hidden";
  });
  return els;
};

const unhideIgnoredEls = (els) => {
  els.forEach((el) => {
    const state = hiddenElState.get(el);
    if (!state) return;
    state.count -= 1;
    if (state.count > 0) return;
    el.style.visibility = state.visibility;
    hiddenElState.delete(el);
  });
};

// Scroll containers (Table's overflow-x-auto wrapper, BarGraph's
// overflow-x-scroll chart strip) render more content than their visible box,
// and they can sit anywhere inside the captured node — the clone would keep
// their clipped width and bake in a scrollbar. Temporarily expand every real
// scroll container (computed overflow auto/scroll only, so text-ellipsis
// elements are untouched) to its full scroll extent for the capture; the
// page reflows for the capture's duration and is restored right after.
const expandScrollContainers = (node) => {
  const restores = [];
  [node, ...Array.from(node.querySelectorAll("*"))].forEach((el) => {
    const needsX = el.scrollWidth > el.clientWidth + 1;
    const needsY = el.scrollHeight > el.clientHeight + 1;
    if (!needsX && !needsY) return;
    const computed = window.getComputedStyle(el);
    const scrollableX = needsX && ["auto", "scroll"].includes(computed.overflowX);
    const scrollableY = needsY && ["auto", "scroll"].includes(computed.overflowY);
    if (!scrollableX && !scrollableY) return;
    const { width, height, maxWidth, maxHeight, overflowX, overflowY } = el.style;
    restores.push({ el, width, height, maxWidth, maxHeight, overflowX, overflowY });
    // scrollWidth/Height exclude borders — add them back from the
    // offset/client difference (border-box sizing app-wide).
    if (scrollableX) {
      el.style.width = `${el.scrollWidth + (el.offsetWidth - el.clientWidth)}px`;
      el.style.maxWidth = "none";
      el.style.overflowX = "visible";
    }
    if (scrollableY) {
      el.style.height = `${el.scrollHeight + (el.offsetHeight - el.clientHeight)}px`;
      el.style.maxHeight = "none";
      el.style.overflowY = "visible";
    }
  });
  return restores;
};

const restoreScrollContainers = (restores) => {
  restores.forEach(({ el, width, height, maxWidth, maxHeight, overflowX, overflowY }) => {
    el.style.width = width;
    el.style.height = height;
    el.style.maxWidth = maxWidth;
    el.style.maxHeight = maxHeight;
    el.style.overflowX = overflowX;
    el.style.overflowY = overflowY;
  });
};

export const downloadNodeAsPng = async (node, fileName) => {
  if (!node) return;

  // Capturing before webfonts finish loading bakes fallback fonts into the
  // PNG — and the font swap can reflow layout, so measure only after it.
  await document.fonts.ready;

  // Expand scroll containers BEFORE measuring: an expanded descendant
  // overflows the capture root, and the root's scroll extent picks that up.
  const scrollRestores = expandScrollContainers(node);

  const width = Math.max(node.offsetWidth, node.scrollWidth);
  const height = Math.max(node.offsetHeight, node.scrollHeight);
  // The canvas is the drawing plus the padding on all four sides, so the area cap has to be measured
  // against the padded size — otherwise a table already close to Safari's limit is tipped over it by the
  // margin and comes back blank.
  const paddedArea = (width + EXPORT_PADDING_PX * 2) * (height + EXPORT_PADDING_PX * 2);
  const pixelRatio = Math.min(3, Math.sqrt(MAX_CANVAS_AREA_PX / Math.max(1, paddedArea)));

  // Building the font-embed CSS fetches every font file — the network-dependent
  // part of the export. Compute it once per page load and reuse it; if it fails,
  // export with system fonts instead of failing.
  if (fontEmbedCssPromise === null) {
    fontEmbedCssPromise = buildFontEmbedCss().catch(() => "");
  }

  const options = {
    // Pin the SVG viewport to the same dimensions the canvas uses below —
    // html-to-image's own measurement (clientWidth + borders) can differ from
    // offsetWidth by a fraction of a pixel, which stretches the rasterized PNG.
    width,
    height,
    backgroundColor: "#FFFFFF",
    fontEmbedCSS: await fontEmbedCssPromise,
    // The clone keeps the node's computed margins, but the export viewport is
    // sized to the node alone — a margin (e.g. mt-4 on overview cards) shifts
    // the clone inside the viewport and crops the far edge. Zero it out.
    // Pinning the clone to the full (scroll) width lets a scroll container lay
    // out all of its content instead of clipping at the visible box.
    style: {
      marginTop: "0",
      marginRight: "0",
      marginBottom: "0",
      marginLeft: "0",
      width: `${width}px`,
      height: `${height}px`,
      maxWidth: "none",
      maxHeight: "none",
    },
  };

  // Excluded elements (the download button rows) must keep their layout box:
  // a clone filter would remove them from the flow and shift everything after
  // them upward, so the export would not match the on-screen spacing. Hide
  // them with visibility (box preserved) for the duration of the capture.
  // Hides are ref-counted so overlapping exports over nested capture nodes
  // restore an element only after its last capture finishes.
  const ignoredEls = hideIgnoredEls(node);

  try {
    // Serialization or decode can still fail transiently — retry before surfacing.
    let lastError;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const svgDataUrl = await toSvg(node, options);
        const svgText = stripXmlInvalidChars(
          decodeURIComponent(svgDataUrl.split(",").slice(1).join(","))
        );
        const pngUrl = await svgTextToPng(svgText, width, height, pixelRatio);

        const link = document.createElement("a");
        link.download = fileName.endsWith(".png") ? fileName : `${fileName}.png`;
        link.href = pngUrl;
        link.click();
        return;
      } catch (e) {
        lastError = e;
      }
    }
    throw lastError;
  } finally {
    unhideIgnoredEls(ignoredEls);
    restoreScrollContainers(scrollRestores);
  }
};

/**
 * Small download-as-PNG icon button. `nodeRef` points at the element to capture;
 * the button itself is excluded from the image via data-download-ignore.
 */
export const DownloadPngButton = ({ nodeRef, fileName = "chart" }) => {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await downloadNodeAsPng(nodeRef?.current, fileName);
    } catch (e) {
      console.error("PNG export failed:", e);
      message.error("Could not generate the image, please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tooltip title="Download as PNG">
      <button
        type="button"
        data-download-ignore="true"
        onClick={handleClick}
        aria-label="Download as PNG"
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center self-center align-middle rounded-md border border-transparent bg-transparent text-gray-900 transition-colors duration-150 hover:border-gray-500 hover:bg-gray-250 focus:outline-none"
      >
        {busy ? (
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
        ) : (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v12" />
            <path d="m7 11 5 4 5-4" />
            <path d="M5 21h14" />
          </svg>
        )}
      </button>
    </Tooltip>
  );
};

/**
 * Wraps arbitrary content (e.g. an antd Table) with a right-aligned
 * download-as-PNG button; the wrapper div is the capture node.
 */
export const PngExportFrame = ({ fileName = "table", className = "", children }) => {
  const nodeRef = useRef(null);
  return (
    <div ref={nodeRef} className={className}>
      <div className="flex justify-end pb-2" data-download-ignore="true">
        <DownloadPngButton nodeRef={nodeRef} fileName={fileName} />
      </div>
      {children}
    </div>
  );
};
